import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../../../common/types';
import { useSMLRouter } from '../../../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../../../routing/sml/SMLRoutes';
import { SMLSettingsTabId } from '../../../../../../settings/tabs/types';
import { planStepTitle } from '../../rolloutDayCopy';
import { useRollout } from '../RolloutContext';
import { platformOfPlanPosts, type PlanStepView } from '../rolloutModel';
import { launchRows, type LaunchSummaryRow } from './launchModel';
import { launchMessages } from './launchMessages';
import { launchPlatformName, PostsLaunchSummary } from './PostsLaunchSummary';
import { autoPostPlatforms, postingSplitOf } from './postingModes';

/**
 * The launch summary of the rollout run sheet (`PostsLaunchSummary`): each post is named by its
 * step and channel, in the order of the plan, and "Open the post" opens the step.
 */
const LaunchSummaryComponent: React.FC<{ steps: PlanStepView[] }> = ({ steps }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const { actions, canAct, openStep, plan, channels } = useRollout();
  const outcome = actions.launchOutcome;
  // QA finding F02: say how many posts go out by themselves and how many come as reminders.
  const split = useMemo(
    () =>
      outcome?.scheduledUuids === undefined
        ? null
        : postingSplitOf(outcome.scheduledUuids, platformOfPlanPosts(steps), autoPostPlatforms(channels)),
    [channels, outcome, steps]
  );
  const openChannels = useCallback(() => router.push(SMLRoutes.promoteChannels), [router]);
  const planRows = useMemo(() => (outcome === null ? [] : launchRows(outcome.needsYou, steps)), [outcome, steps]);
  const rows = useMemo<LaunchSummaryRow[]>(
    () =>
      planRows.map((row) => {
        const name = launchPlatformName(row.platform);
        return {
          postUuid: row.postUuid,
          platform: row.platform,
          reason: row.reason,
          detail: row.detail,
          fix: row.fix,
          title:
            row.step === null
              ? intl.formatMessage(launchMessages.rowNameNoStep, { name })
              : intl.formatMessage(launchMessages.rowName, { step: planStepTitle(intl, plan, row.step.day), name }),
        };
      }),
    [intl, plan, planRows]
  );

  const { launch } = actions;
  const fix = useCallback(
    (row: LaunchSummaryRow) => {
      const step = planRows.find((one) => one.postUuid === row.postUuid)?.step ?? null;
      switch (row.fix) {
        case 'answer':
          // "Answer" asks one time for every post that waits for an answer.
          launch(
            'plan',
            planRows.filter((one) => one.fix === 'answer').map((one) => one.postUuid)
          ).catch(console.error);
          break;
        case 'reconnect':
          router.push(SMLRoutes.promoteChannels);
          break;
        case 'plans':
          router.push(SMLRoutes.settings, { tab: SMLSettingsTabId.subscription });
          break;
        case 'openPost':
          if (step !== null) {
            openStep(step.key, row.platform);
          } else {
            router.push(SMLRoutes.promoteCalendar, { post: row.postUuid });
          }
      }
    },
    [launch, openStep, planRows, router]
  );

  return (
    <PostsLaunchSummary
      outcome={outcome}
      rows={rows}
      isDisabled={!canAct || actions.busy !== null}
      onFix={fix}
      onDismiss={actions.dismissLaunchOutcome}
      split={split}
      onConnectChannels={canAct ? openChannels : undefined}
    />
  );
};

export const LaunchSummary = TypedMemo(LaunchSummaryComponent);
